import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import { leaveRequestsApi } from '../services/api';

const EMPTY_FORM = {
  leave_type: 'holiday',
  start_date: '',
  end_date: '',
  start_day_portion: 'full',
  end_day_portion: 'full',
  worker_note: '',
};

const STATUS_COLOR = {
  pending: 'warning', queried: 'error', approved: 'success', rejected: 'default', cancelled: 'default',
};

const titleCase = (value) => value ? value[0].toUpperCase() + value.slice(1) : '';
const formatDays = (value) => {
  const days = Number(value || 0);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
};
const formatDate = (value) => value ? new Date(`${value}T12:00:00`).toLocaleDateString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
}) : '-';

function LeaveTypeBreakdown({ values = {} }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
      {['holiday', 'sick', 'other'].map((type) => (
        <Box key={type} sx={{ minWidth: 72, textAlign: 'right' }}>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {titleCase(type)}
          </Typography>
          <Typography component="span" variant="body2" fontWeight={600}>
            {formatDays(values[type])}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function LeaveRequestDialog({ open, request, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(request ? {
      leave_type: request.leave_type,
      start_date: request.start_date,
      end_date: request.end_date,
      start_day_portion: request.start_day_portion,
      end_day_portion: request.end_day_portion,
      worker_note: request.worker_note || '',
    } : EMPTY_FORM);
    setError('');
  }, [open, request]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const submit = async () => {
    if (!form.start_date || !form.end_date) {
      setError('Choose a start and end date.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (request) await leaveRequestsApi.update(request.leave_request_id, form);
      else await leaveRequestsApi.create(form);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to submit the request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{request ? 'Update Time-Off Request' : 'Request Time Off'}</DialogTitle>
      <DialogContent>
        {request?.query_reason && <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>{request.query_reason}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 1, mb: 2 }}>{error}</Alert>}
        <FormControl fullWidth margin="normal">
          <InputLabel>Leave type</InputLabel>
          <Select label="Leave type" value={form.leave_type} onChange={update('leave_type')}>
            <MenuItem value="holiday">Holiday</MenuItem>
            <MenuItem value="sick">Sick</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth margin="normal" type="date" label="Start date" value={form.start_date} onChange={update('start_date')} InputLabelProps={{ shrink: true }} />
            <FormControl fullWidth>
              <InputLabel>Start day</InputLabel>
              <Select label="Start day" value={form.start_day_portion} onChange={update('start_day_portion')}>
                <MenuItem value="full">Full day</MenuItem><MenuItem value="half">Half day</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth margin="normal" type="date" label="End date" value={form.end_date} onChange={update('end_date')} InputLabelProps={{ shrink: true }} />
            <FormControl fullWidth>
              <InputLabel>End day</InputLabel>
              <Select label="End day" value={form.end_day_portion} onChange={update('end_day_portion')}>
                <MenuItem value="full">Full day</MenuItem><MenuItem value="half">Half day</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <TextField fullWidth multiline rows={3} margin="normal" label="Note (optional)" value={form.worker_note} onChange={update('worker_note')} inputProps={{ maxLength: 1000 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={loading}>
          {loading ? <CircularProgress size={22} /> : (request ? 'Resubmit' : 'Submit Request')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function WorkerLeaveSection() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await leaveRequestsApi.getMyRequests();
      setRequests(response.data.requests || []);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load time-off requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const closeDialog = () => { setDialogOpen(false); setEditing(null); };
  const saved = () => { closeDialog(); load(); };
  const cancel = async (requestId) => {
    try { await leaveRequestsApi.cancel(requestId); await load(); }
    catch (err) { setError(err?.response?.data?.error || 'Failed to cancel the request.'); }
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BeachAccessIcon /> Time Off</Typography>
          <Typography variant="body2" color="text.secondary">Holiday, sick leave, and other absence requests.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>Request Time Off</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <CircularProgress /> : requests.length === 0 ? (
        <Typography color="text.secondary">No time-off requests yet.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell>Type</TableCell><TableCell>Dates</TableCell><TableCell>Days</TableCell><TableCell>Status</TableCell><TableCell>Response</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>{requests.map((item) => (
              <TableRow key={item.leave_request_id}>
                <TableCell>{titleCase(item.leave_type)}</TableCell>
                <TableCell>{formatDate(item.start_date)}{item.end_date !== item.start_date ? ` – ${formatDate(item.end_date)}` : ''}</TableCell>
                <TableCell>{item.total_days}</TableCell>
                <TableCell><Chip size="small" label={item.status === 'queried' ? 'Needs response' : titleCase(item.status)} color={STATUS_COLOR[item.status]} /></TableCell>
                <TableCell>{item.query_reason || item.manager_note || '-'}</TableCell>
                <TableCell align="right">
                  {(item.status === 'pending' || item.status === 'queried') && <Button size="small" onClick={() => { setEditing(item); setDialogOpen(true); }}>Edit</Button>}
                  {!['cancelled', 'rejected'].includes(item.status) && <Button size="small" color="error" onClick={() => cancel(item.leave_request_id)}>Cancel</Button>}
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </TableContainer>
      )}
      <LeaveRequestDialog open={dialogOpen} request={editing} onClose={closeDialog} onSaved={saved} />
    </Paper>
  );
}

function ReviewDialog({ action, item, onClose, onComplete }) {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setNote(''); setError(''); }, [action, item]);
  if (!action || !item) return null;
  const requiresNote = action !== 'approve';
  const submit = async () => {
    if (requiresNote && !note.trim()) { setError('A reason is required.'); return; }
    setLoading(true);
    try {
      if (action === 'approve') await leaveRequestsApi.approve(item.leave_request_id, note);
      if (action === 'query') await leaveRequestsApi.query(item.leave_request_id, note);
      if (action === 'reject') await leaveRequestsApi.reject(item.leave_request_id, note);
      onComplete();
    } catch (err) { setError(err?.response?.data?.error || `Failed to ${action} the request.`); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{titleCase(action)} Leave Request</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mt: 1, mb: 2 }}>{error}</Alert>}
        <Typography sx={{ mt: 1 }}>{item.worker_name}: {formatDate(item.start_date)} – {formatDate(item.end_date)}</Typography>
        <TextField fullWidth multiline rows={3} margin="normal" label={action === 'query' ? 'Question for team member' : 'Manager note'} required={requiresNote} value={note} onChange={(event) => setNote(event.target.value)} />
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" color={action === 'reject' ? 'error' : 'primary'} onClick={submit} disabled={loading}>{loading ? <CircularProgress size={22} /> : titleCase(action)}</Button></DialogActions>
    </Dialog>
  );
}

export function OfficeLeaveSection({ workerDeptMap = {}, selectedDepartment = '', onUpdate = () => {} }) {
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [summaryWorkerId, setSummaryWorkerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [review, setReview] = useState({ action: null, item: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [requestResponse, summaryResponse] = await Promise.all([
        leaveRequestsApi.getAll(), leaveRequestsApi.getAnnualSummary(year),
      ]);
      setRequests(requestResponse.data.requests || []);
      setSummary(summaryResponse.data.workers || []);
      setError('');
    } catch (err) { setError(err?.response?.data?.error || 'Failed to load leave requests.'); }
    finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const filteredRequests = useMemo(() => selectedDepartment
    ? requests.filter((item) => workerDeptMap[item.worker_id] === selectedDepartment) : requests,
  [requests, selectedDepartment, workerDeptMap]);
  const departmentSummary = useMemo(() => selectedDepartment
    ? summary.filter((item) => workerDeptMap[item.worker_id] === selectedDepartment) : summary,
  [summary, selectedDepartment, workerDeptMap]);
  const summaryWorkerOptions = useMemo(() => [...departmentSummary].sort((a, b) =>
    (a.worker_name || '').localeCompare(b.worker_name || '')
  ), [departmentSummary]);
  useEffect(() => {
    if (summaryWorkerId && !summaryWorkerOptions.some((item) => String(item.worker_id) === summaryWorkerId)) {
      setSummaryWorkerId('');
    }
  }, [summaryWorkerId, summaryWorkerOptions]);
  const filteredSummary = useMemo(() => summaryWorkerId
    ? departmentSummary.filter((item) => String(item.worker_id) === summaryWorkerId)
    : departmentSummary,
  [departmentSummary, summaryWorkerId]);
  const reviewQueue = filteredRequests.filter((item) => ['pending', 'queried'].includes(item.status));

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <CircularProgress /> : (
        <>
          <Typography variant="h6" gutterBottom>Requests needing review ({reviewQueue.length})</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table size="small" aria-label="Leave requests needing review"><TableHead><TableRow><TableCell>Name</TableCell><TableCell>Type</TableCell><TableCell>Dates</TableCell><TableCell>Days</TableCell><TableCell>Status</TableCell><TableCell>Note</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
              <TableBody>{reviewQueue.length === 0 ? <TableRow><TableCell colSpan={7}>No leave requests need review.</TableCell></TableRow> : reviewQueue.map((item) => (
                <TableRow key={item.leave_request_id}><TableCell>{item.worker_name}</TableCell><TableCell>{titleCase(item.leave_type)}</TableCell><TableCell>{formatDate(item.start_date)} – {formatDate(item.end_date)}</TableCell><TableCell>{item.total_days}</TableCell><TableCell><Chip size="small" label={item.status === 'queried' ? 'Queried' : 'Pending'} color={STATUS_COLOR[item.status]} /></TableCell><TableCell>{item.worker_note || '-'}</TableCell><TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><Button size="small" color="success" onClick={() => setReview({ action: 'approve', item })}>Approve</Button><Button size="small" color="warning" onClick={() => setReview({ action: 'query', item })}>Query</Button><Button size="small" color="error" onClick={() => setReview({ action: 'reject', item })}>Reject</Button></TableCell></TableRow>
              ))}</TableBody></Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2, flexDirection: { xs: 'column', md: 'row' }, mb: 2 }}>
            <Box><Typography variant="h6">Leave booked by calendar year</Typography><Typography variant="body2" color="text.secondary">Approved/booked and pending leave are split into holiday, sick, and other.</Typography></Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="leave-summary-worker-label">Name</InputLabel>
                <Select labelId="leave-summary-worker-label" label="Name" value={summaryWorkerId} onChange={(event) => setSummaryWorkerId(String(event.target.value))}>
                  <MenuItem value="">All team members</MenuItem>
                  {summaryWorkerOptions.map((worker) => (
                    <MenuItem key={worker.worker_id} value={String(worker.worker_id)}>{worker.worker_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" type="number" label="Calendar year" value={year} onChange={(event) => setYear(Number(event.target.value))} inputProps={{ min: 2000, max: 2100 }} />
            </Box>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Leave booked by calendar year"><TableHead><TableRow><TableCell>Name</TableCell><TableCell align="right">Approved / booked by type</TableCell><TableCell align="right">Pending by type</TableCell></TableRow></TableHead>
              <TableBody>{filteredSummary.length === 0 ? <TableRow><TableCell colSpan={3}>No leave requests in {year}.</TableCell></TableRow> : filteredSummary.map((worker) => (
                <TableRow key={worker.worker_id}>
                  <TableCell>{worker.worker_name}</TableCell>
                  <TableCell align="right"><LeaveTypeBreakdown values={worker.approved_by_type} /></TableCell>
                  <TableCell align="right"><LeaveTypeBreakdown values={worker.pending_by_type} /></TableCell>
                </TableRow>
              ))}</TableBody></Table>
          </TableContainer>
        </>
      )}
      <ReviewDialog action={review.action} item={review.item} onClose={() => setReview({ action: null, item: null })} onComplete={() => { setReview({ action: null, item: null }); load(); onUpdate(); }} />
    </Box>
  );
}
